import { useCallback } from 'react'
import { Variable } from '@mobileTypes'
import {
  useCopyMobileGlobalVariableMutation,
  useCreateMobileGlobalVariableMutation,
  useDeleteMobileGlobalVariableMutation,
  useFetchAllMobileGlobalVariablesQuery,
  useUpdateMobileGlobalVariableMutation,
} from '@mobileServices'

export const useMobileGlobalVariables = () => {
  const { data: variables = [], isLoading: loading } = useFetchAllMobileGlobalVariablesQuery()
  const [createMobileGlobalVariable] = useCreateMobileGlobalVariableMutation()
  const [updateMobileGlobalVariable] = useUpdateMobileGlobalVariableMutation()
  const [deleteMobileGlobalVariable] = useDeleteMobileGlobalVariableMutation()
  const [copyMobileGlobalVariable] = useCopyMobileGlobalVariableMutation()

  const createGlobalVariable = useCallback(
    (variable: Omit<Variable, 'id'>): Promise<Variable> => createMobileGlobalVariable(variable).unwrap(),
    [createMobileGlobalVariable]
  )

  const updateGlobalVariable = useCallback(
    (internalId: string, variable: Partial<Variable>): Promise<Variable> => {
      const existing = variables.find(v => v.internalId === internalId)
      if (!existing?.id) {
        throw new Error(`Global variable "${internalId}" not found`)
      }

      const merged: Variable = {
        ...existing,
        ...variable,
      }

      return updateMobileGlobalVariable(merged).unwrap()
    },
    [variables, updateMobileGlobalVariable]
  )

  const deleteGlobalVariable = useCallback(
    async (internalId: string): Promise<void> => {
      const existing = variables.find(v => v.internalId === internalId)
      if (!existing?.id) {
        throw new Error(`Global variable "${internalId}" not found`)
      }

      await deleteMobileGlobalVariable(String(existing.id)).unwrap()
    },
    [variables, deleteMobileGlobalVariable]
  )

  const getGlobalVariableByInternalId = useCallback(
    (internalId: string) => {
      return variables.find(v => v.internalId === internalId) || null
    },
    [variables]
  )

  const copyGlobalVariable = useCallback(
    async (internalId: string, newInternalId: string): Promise<Variable | null> => {
      const variable = getGlobalVariableByInternalId(internalId)
      if (!variable?.id) {
        throw new Error(`Global variable "${internalId}" not found`)
      }

      try {
        return await copyMobileGlobalVariable({
          id: String(variable.id),
          internalId: newInternalId,
        }).unwrap()
      } catch {
        return null
      }
    },
    [getGlobalVariableByInternalId, copyMobileGlobalVariable]
  )

  return {
    variables,
    loading,
    createGlobalVariable,
    updateGlobalVariable,
    deleteGlobalVariable,
    getGlobalVariableByInternalId,
    copyGlobalVariable,
  }
}
